import React, { useState, useMemo, memo } from 'react';
import { Spin, Table } from 'antd';
import { formatSecurityAmount } from './format';

/**
 * Minimal structural shape of an Apex holding row used by this component.
 * Each portal has a richer `Position` type from the generated Apex client —
 * only the four fields below are read here.
 */
export interface ApexHoldingLike {
  symbol?: string;
  description?: string;
  amount?: string;
  stock_unit_price?: string | null;
  [key: string]: unknown;
}

/**
 * Minimal structural shape of an Apex account used by this component.
 */
export interface ApexAccountLike {
  id: string;
  holdings?: ApexHoldingLike[];
  [key: string]: unknown;
}

/**
 * Minimal structural shape of a Plaid bank account used for institution
 * lookup. Each portal has its own richer `BankAccountData` type — only the
 * two fields below are referenced inside this component.
 */
export interface PlaidAccountLike {
  account_id?: string;
  institution_name?: string;
  [key: string]: unknown;
}

export interface AllHoldingsTabProps {
  renderCostBasis?: (
    accountId: string,
    holding: ApexHoldingLike,
    gain: boolean,
    external: boolean,
  ) => React.ReactNode;
  apexAccounts: ApexAccountLike[];
  plaidAccounts: PlaidAccountLike[];
  plaidHoldings: any | null;
  /** Whether to show the loading spinner */
  isLoading: boolean;
  /**
   * When true, weights are recalculated against the filtered total whenever
   * the user selects a single institution. When false, weights stay relative
   * to the full holdings list.
   *
   * Defaults to `false`.
   */
  recalculateWeightsOnFilter?: boolean;
  /**
   * If provided, passed to the AntD Table's `scroll.y` prop (vertical scroll height).
   * Defaults to undefined (no scroll).
   */
  tableScrollY?: number;
}

const AllHoldingsTab: React.FC<AllHoldingsTabProps> = ({
  renderCostBasis,
  apexAccounts,
  plaidAccounts,
  plaidHoldings,
  isLoading,
  recalculateWeightsOnFilter = false,
  tableScrollY,
}) => {
  const [selectedInstitution, setSelectedInstitution] = useState<string>('all');

  const allHoldingsLoaded = useMemo(() => {
    return apexAccounts.every((account) => account.holdings !== undefined);
  }, [apexAccounts]);

  const allHoldingsData = useMemo(() => {
    const isHoldingsnull = apexAccounts.length === 0 && plaidHoldings === null;
    if (isHoldingsnull || !allHoldingsLoaded) return [];

    const apexHoldingsArr =
      apexAccounts?.flatMap(
        (account) =>
          account.holdings?.map((holding) => ({
            accountId: account.id,
            sourceHolding: holding,
            external: false,
            security: `${holding.symbol} (${holding.description || holding.symbol})`,
            institution: 'Wavvest',
            shares: parseFloat(holding.amount || '0'),
            price:
              holding.symbol?.toLowerCase() === 'usd'
                ? 1
                : parseFloat(holding.stock_unit_price || '0'),
            value:
              parseFloat(holding.amount || '0') *
              (holding.symbol?.toLowerCase() === 'usd'
                ? 1
                : parseFloat(holding.stock_unit_price || '0')),
            weight: 0,
            key: `${account.id}-${holding.asset_id || holding.symbol}`,
          })) || [],
      ) || [];

    const plaidHoldingsArr =
      plaidHoldings?.securities?.flatMap(
        (security: any) =>
          security.holdings?.map((holding: any) => ({
            accountId: holding.account_id,
            sourceHolding: holding,
            external: true,
            security: `${security.ticker_symbol || security.name} (${security.name})`,
            institution:
              plaidAccounts?.find((acc) => acc.account_id === holding.account_id)
                ?.institution_name || 'Unknown',
            shares: parseFloat(holding.quantity || '0'),
            price: security.close_price || holding.price || 0,
            value: holding.institution_value || holding.value || 0,
            weight: 0,
            key: `${holding.account_id}-${security.security_id}`,
          })) || [],
      ) || [];

    const allHoldings = [...apexHoldingsArr, ...plaidHoldingsArr];
    const totalValue = allHoldings.reduce((sum, holding) => sum + holding.value, 0);
    return allHoldings.map((holding) => ({
      ...holding,
      weight: totalValue > 0 ? ((holding.value / totalValue) * 100).toFixed(2) + '%' : '0%',
    }));
  }, [apexAccounts, plaidHoldings, plaidAccounts, allHoldingsLoaded]);

  const institutions = useMemo(() => {
    const institutionSet = new Set(allHoldingsData.map((holding) => holding.institution));
    return ['all', ...Array.from(institutionSet)];
  }, [allHoldingsData]);

  const filteredHoldingsData = useMemo(() => {
    const filtered =
      selectedInstitution === 'all'
        ? allHoldingsData
        : allHoldingsData.filter((holding) => holding.institution === selectedInstitution);

    if (!recalculateWeightsOnFilter) {
      return filtered;
    }

    // Recalculate weights based on filtered holdings
    const filteredTotalValue = filtered.reduce((sum, holding) => sum + holding.value, 0);

    return filtered.map((holding) => ({
      ...holding,
      weight:
        filteredTotalValue > 0
          ? ((holding.value / filteredTotalValue) * 100).toFixed(2) + '%'
          : '0%',
    }));
  }, [allHoldingsData, selectedInstitution, recalculateWeightsOnFilter]);

  // Right-align figures once cost columns are present (matches the v2 design);
  // callers without cost basis keep their existing layout.
  const numericAlign = renderCostBasis ? ('right' as const) : undefined;
  const holdingsColumns = [
    { title: 'Security', dataIndex: 'security', key: 'security' },
    { title: 'Institution', dataIndex: 'institution', key: 'institution' },
    {
      title: 'Shares',
      align: numericAlign,
      dataIndex: 'shares',
      key: 'shares',
      render: (value: number) => value.toFixed(2),
    },
    {
      title: 'Price',
      align: numericAlign,
      dataIndex: 'price',
      key: 'price',
      render: (value: number) => formatSecurityAmount(value),
    },
    {
      title: 'Value',
      align: numericAlign,
      dataIndex: 'value',
      key: 'value',
      render: (value: number) => formatSecurityAmount(value),
    },
    ...(renderCostBasis
      ? [
          {
            title: 'Cost basis',
            align: numericAlign,
            key: 'cost-basis',
            render: (_value: unknown, record: (typeof allHoldingsData)[number]) =>
              renderCostBasis(record.accountId, record.sourceHolding, false, record.external),
          },
          {
            title: 'Gain / Loss',
            align: numericAlign,
            key: 'gain-loss',
            render: (_value: unknown, record: (typeof allHoldingsData)[number]) =>
              renderCostBasis(record.accountId, record.sourceHolding, true, record.external),
          },
        ]
      : []),
    { title: 'Weight', align: numericAlign, dataIndex: 'weight', key: 'weight' },
  ];

  return (
    <div className="holdings-content">
      {isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
          }}
        >
          <Spin size="large" tip="Loading holdings data..." />
        </div>
      ) : (
        <>
          <div className="institution-filters">
            {institutions.map((institution) => (
              <button
                key={institution}
                className={`filter-button ${selectedInstitution === institution ? 'active' : ''}`}
                onClick={() => setSelectedInstitution(institution)}
              >
                {institution === 'all' ? 'All' : institution}
              </button>
            ))}
          </div>
          <div className="holdings-table-container">
            <Table
              columns={holdingsColumns}
              dataSource={filteredHoldingsData}
              pagination={false}
              size="small"
              rowKey="key"
              scroll={
                renderCostBasis
                  ? { x: 1050, ...(tableScrollY ? { y: tableScrollY } : {}) }
                  : tableScrollY
                    ? { y: tableScrollY }
                    : undefined
              }
            />
          </div>
        </>
      )}
    </div>
  );
};

export default memo(AllHoldingsTab);
